import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { QuickInterval } from '@/api/models/parking/lib/parking.types'
import { cn } from '@/app/utils'
import { DEFAULT_TIMEZONE } from '@/components/entities/requests/model/constants'
import { toMskDayString } from '@/utils/datetime'
import {
  getDaysPluralization,
  getHoursPluralization
} from '@/utils/string-formatting'

import { AppButton } from '../../button'
import { getRangeFromQuickInterval } from '../utils/getRangeFromQuickInterval'
import { DateRange, PickerMode } from './Calendar.types'
import {
  DateCalendarPanel,
  HoursCalendarPanel,
  MonthCalendarPanel
} from './panel'

type Props = {
  className?: string
  dateRange?: DateRange
  onChange: (range?: DateRange) => void
  fromMonth?: Date
  showSelectedDate?: boolean
  close?: () => void
  onApply?: () => void
  disabled?: {
    before?: Date
    after?: Date
  }
  calendarType?: 'calendar' | 'hours'
  customQuickFilters?: QuickInterval[]
  individualSlots?: {
    bordered: { from: string; to: string }[]
    disabled: { from: string; to: string }[]
  }
  isLoading?: boolean
  onMonthChange?: (month: Date) => void
  /**
   * Режим «якоря»: начало промежутка всегда зафиксировано этой датой.
   * Клик по дню или месяцу двигает только конец промежутка и не сбрасывает выбор.
   */
  anchorFrom?: Date
  /**
   * Кнопка «Выбрать» неактивна, пока конец промежутка не выбран,
   * и дефолтный быстрый фильтр при пустом выборе не подставляется.
   */
  applyRequiresSelection?: boolean
  /** В режиме месяцев выбранной датой считается последний день выбранного месяца */
  selectMonthAsRangeEnd?: boolean
  /**
   * В бейдже и счётчике показывать только конец промежутка: сам промежуток
   * нужен лишь для наглядности, выбранной считается одна дата.
   */
  showOnlyRangeEnd?: boolean
  /** Календарь не открывается на днях раньше этой даты, даже если промежуток начинается раньше */
  firstVisibleDate?: Date
}

const DateRangePicker: FC<Props> = ({
  dateRange = { from: undefined, to: undefined },
  fromMonth,
  showSelectedDate = true,
  onChange,
  close,
  onApply,
  className,
  calendarType = 'calendar',
  disabled,
  customQuickFilters = [],
  individualSlots,
  isLoading,
  onMonthChange,
  anchorFrom,
  applyRequiresSelection = false,
  selectMonthAsRangeEnd = false,
  showOnlyRangeEnd = false,
  firstVisibleDate
}) => {
  const firstVisibleTimestamp = firstVisibleDate?.getTime()

  const resolveVisibleMonth = useCallback(
    (date: Date) =>
      firstVisibleTimestamp && date.getTime() < firstVisibleTimestamp
        ? new Date(firstVisibleTimestamp)
        : date,
    [firstVisibleTimestamp]
  )

  const [range, setRange] = useState<DateRange | undefined>(dateRange)
  const [month, setMonth] = useState(() =>
    resolveVisibleMonth(anchorFrom || new Date())
  )
  const [pickerMode, setPickerMode] = useState<PickerMode>(calendarType)
  const isHoursType = calendarType === 'hours'
  const dateRangeFromTimestamp = dateRange?.from?.getTime()
  const dateRangeToTimestamp = dateRange?.to?.getTime()

  useEffect(() => {
    setRange(dateRange)

    if (dateRange?.from) {
      setMonth(resolveVisibleMonth(dateRange.from))
    }
  }, [
    dateRange,
    dateRangeFromTimestamp,
    dateRangeToTimestamp,
    resolveVisibleMonth
  ])

  useEffect(() => {
    setPickerMode(calendarType)
  }, [calendarType])

  const hasInvalidSlotOverlap = useMemo(() => {
    const slots = [
      ...(individualSlots?.disabled ?? []),
      ...(individualSlots?.bordered ?? [])
    ]

    if (!range?.from || !range?.to || slots.length === 0) {
      return false
    }

    const unit = isHoursType ? 'hour' : 'day'

    const zonedRange = (date: Date) =>
      isHoursType ? dayjs(date).tz(DEFAULT_TIMEZONE) : dayjs(date)
    const zonedSlot = (date: string) =>
      isHoursType
        ? dayjs(date).tz(DEFAULT_TIMEZONE)
        : dayjs(toMskDayString(date))
    const rangeFrom = zonedRange(range.from).startOf(unit)
    const rangeTo = zonedRange(range.to).startOf(unit)

    return slots.some(({ from, to }) => {
      const slotFrom = zonedSlot(from).startOf(unit)
      const slotTo = zonedSlot(to).startOf(unit)

      return isHoursType
        ? rangeFrom.isBefore(slotTo) && rangeTo.isAfter(slotFrom)
        : rangeFrom.isSameOrBefore(slotTo) && rangeTo.isSameOrAfter(slotFrom)
    })
  }, [
    range?.from,
    range?.to,
    individualSlots?.disabled,
    individualSlots?.bordered,
    isHoursType
  ])

  const resolvedDisabled = useMemo(
    () =>
      disabled || {
        before: new Date(2023, 0, 1),
        after: new Date()
      },
    [disabled]
  )

  const disabledMatchers = useMemo(
    () => [
      ...(resolvedDisabled.before ? [{ before: resolvedDisabled.before }] : []),
      ...(resolvedDisabled.after ? [{ after: resolvedDisabled.after }] : [])
    ],
    [resolvedDisabled.after, resolvedDisabled.before]
  )

  const getDefaultFilterRange = () => {
    const defaultFilter = customQuickFilters.find(filter => filter.isDefault)

    if (!defaultFilter) {
      return undefined
    }

    return getRangeFromQuickInterval(defaultFilter)
  }

  const isApplyDisabled = applyRequiresSelection && !range?.to

  // при выборе одной даты якорь сам по себе ещё не выбор, поэтому бейдж
  // не показываем, пока не выбран конец промежутка
  const resolvedShowSelectedDate =
    showSelectedDate && (!showOnlyRangeEnd || Boolean(range?.to))

  const handleApply = () => {
    if (hasInvalidSlotOverlap || isApplyDisabled) {
      return
    }

    let appliedRange = undefined

    if (range?.from) {
      appliedRange = range.to ? range : { from: range.from, to: range.from }
    } else if (!applyRequiresSelection) {
      // если ничего не выбрано, при нажатии "Выбрать" применяем дефолтный быстрый фильтр
      appliedRange = getDefaultFilterRange()
    }

    onChange(appliedRange)
    onApply?.()
    close?.()
  }

  const onRangeReset = () => {
    if (anchorFrom) {
      // якорь остаётся, сбрасывается только конец промежутка
      setRange({ from: anchorFrom, to: undefined })
      setMonth(anchorFrom)
      return
    }

    const defaultRange = getDefaultFilterRange()

    if (defaultRange) {
      setRange(defaultRange)
      setMonth(defaultRange.from)
      return
    }

    setRange(undefined)
    setMonth(anchorFrom || new Date())
  }

  const onMonthClick = () => {
    const toggledMonthMode = pickerMode === 'months' ? 'calendar' : 'months'

    if (!isHoursType) {
      setPickerMode(toggledMonthMode)
      return
    }

    if (pickerMode === 'hours') {
      setPickerMode('calendar')
      return
    }

    setPickerMode(toggledMonthMode)
  }

  const onSelectDate = (selectedDate: Date, selectedRange?: DateRange) => {
    if (anchorFrom) {
      if (!dayjs(selectedDate).isAfter(dayjs(anchorFrom), 'day')) {
        return
      }

      setRange({ from: anchorFrom, to: selectedDate })
      return
    }

    if (
      dayjs(selectedDate).isSame(range?.to) ||
      dayjs(selectedDate).isSame(range?.from)
    ) {
      setRange({ from: undefined, to: undefined })
    } else {
      setRange(selectedRange)
    }

    if (isHoursType) {
      setPickerMode('hours')
    }
  }

  const onSelectMonthForNavigation = (monthRange?: DateRange) => {
    const selectedMonth = monthRange?.from || monthRange?.to

    if (!selectedMonth) {
      return
    }

    if (selectMonthAsRangeEnd) {
      // выбранной датой считается последний день выбранного месяца
      const monthEnd = dayjs(selectedMonth).endOf('month').startOf('day')

      // конец промежутка должен быть строго позже якоря или нижней границы
      const minRangeEnd = anchorFrom || resolvedDisabled.before

      if (minRangeEnd && !monthEnd.isAfter(dayjs(minRangeEnd), 'day')) {
        return
      }

      setRange({
        from: anchorFrom || range?.from || selectedMonth,
        to: monthEnd.toDate()
      })
      handleMonthChange(selectedMonth)
      return
    }

    // месяц нужен только для навигации по календарю дней
    handleMonthChange(selectedMonth)
    setPickerMode('calendar')
  }

  const onSelectDateForHours = (selectedDate?: Date) => {
    if (!selectedDate) {
      return
    }

    handleMonthChange(selectedDate)
    setPickerMode('hours')
  }

  const onHoursDateChange = (nextDate: Date) => {
    handleMonthChange(nextDate)
  }

  const handleMonthChange = useCallback(
    (nextMonth: Date) => {
      setMonth(nextMonth)
      onMonthChange?.(nextMonth)
    },
    [onMonthChange]
  )

  const selectedDaysCount =
    range?.from && range?.to
      ? dayjs(range.to).diff(dayjs(range.from), 'day')
      : 0

  const selectedHoursCount =
    range?.from && range?.to
      ? dayjs(range.to).diff(dayjs(range.from), 'hour')
      : 0

  const shouldShowSelectedCount = isHoursType
    ? selectedHoursCount > 0
    : Boolean(range?.from) && !isApplyDisabled && !showOnlyRangeEnd

  const selectedCount = isHoursType ? selectedHoursCount : selectedDaysCount + 1

  const selectedCountLabel = isHoursType
    ? getHoursPluralization(selectedHoursCount)
    : getDaysPluralization(selectedDaysCount)

  return (
    <div
      className={cn(
        'absolute z-[60] mt-8 flex w-full flex-col items-center justify-center gap-8 rounded-12 border border-grey-20 bg-white p-12 xxs:p-0',
        'xxs:relative xxs:h-full xxs:justify-between xxs:border-0',
        className
      )}
    >
      <div className="flex w-full flex-col items-center gap-8">
        {pickerMode === 'calendar' && (
          <DateCalendarPanel
            isHoursType={isHoursType}
            range={range}
            month={month}
            setMonth={handleMonthChange}
            fromMonth={fromMonth}
            resolvedDisabled={resolvedDisabled}
            disabledMatchers={disabledMatchers}
            showSelectedDate={resolvedShowSelectedDate}
            showOnlyRangeEnd={showOnlyRangeEnd}
            onMonthClick={onMonthClick}
            onHoursDateChange={onHoursDateChange}
            onSelectDateForHours={onSelectDateForHours}
            onSelectDate={onSelectDate}
            onRangeReset={onRangeReset}
            isLoading={isLoading}
            individualSlots={individualSlots}
          />
        )}
        {pickerMode === 'months' && (
          <MonthCalendarPanel
            range={range}
            month={month}
            setMonth={handleMonthChange}
            fromMonth={fromMonth}
            showSelectedDate={resolvedShowSelectedDate}
            showOnlyRangeEnd={showOnlyRangeEnd}
            displayRange={selectMonthAsRangeEnd ? range : undefined}
            resolvedDisabled={resolvedDisabled}
            onMonthClick={onMonthClick}
            onSelectMonth={onSelectMonthForNavigation}
            onRangeReset={onRangeReset}
          />
        )}
        {pickerMode === 'hours' && (
          <HoursCalendarPanel
            range={range}
            month={month}
            setMonth={handleMonthChange}
            resolvedDisabled={resolvedDisabled}
            onMonthClick={onMonthClick}
            onHoursDateChange={onHoursDateChange}
            setRange={setRange}
            showSelectedDate={showSelectedDate}
            onRangeReset={onRangeReset}
            individualSlots={individualSlots}
            isLoading={isLoading}
          />
        )}

        {!isLoading &&
          (!isHoursType || (isHoursType && pickerMode === 'hours')) &&
          hasInvalidSlotOverlap && (
            <div className="flex w-full items-center gap-4 text-red-70">
              <ExclamationCircleIcon className="h-18 w-18" />
              <p className="font-normal text-16">Недопустимый интервал</p>
            </div>
          )}
      </div>

      {!isLoading &&
        (!isHoursType || (isHoursType && pickerMode === 'hours')) && (
          <AppButton
            type="button"
            className="mt-16 w-full"
            variant="secondary"
            disabled={hasInvalidSlotOverlap || isApplyDisabled}
            onClick={handleApply}
          >
            Выбрать
            {shouldShowSelectedCount ? (
              <span className="text-bronze-40">
                <span className="text-grey-10">:</span> {selectedCount}{' '}
                {selectedCountLabel}
              </span>
            ) : null}
          </AppButton>
        )}
    </div>
  )
}

export default DateRangePicker
